import { AppContext } from "@lightspeed-invoicing/core-client/frontend"
import React from "react"
import { FormattedDate, FormattedMessage, FormattedNumber, injectIntl } from "react-intl"
import { useQuery } from "react-query"
import { Link } from "react-router-dom"
import useListingPagination from "../../common/hooks/UseListingPagination"
import { BasicRow } from "../../common/listing/ListingTypes"
import ListingWrapper from "../../common/listing/ListingWrapper"
import { CreditMemoElastic } from "../../state/listing/credit-memo/models/creditMemoElastic"
import { PaymentElastic } from "../../state/listing/payment/models"
import { PaymentListingService } from "../../state/listing/payment/service"
import Payment from "../../state/payment/Payment"

type Params = {
    payment: Payment,
    creditMemo : CreditMemoElastic
}

const PaymentDetailsRefundListing = (props: Params) => {

    const paymentId = props.payment.getId()

    const refundsPaymentIds = props.payment.getRefunds().length > 0 ?
        props.payment.getRefunds().map((ref) => ref.attributes.paymentId) :
        props.payment.getRefundedPayments().map((ref) => ref.attributes.paymentId)


    const {
        paginatedSearch,
        updateTotalItemsCount,
        pagination
    } = useListingPagination({})

    const { data: paymentRefundsData , isLoading, error  } = useQuery(
        ["findRefundsPayments", paymentId, paginatedSearch], () => {
            return PaymentListingService.findPaymentsByIds(refundsPaymentIds, paginatedSearch)
        }
    )

    const getRefundAmountByPaymentId = (paymentId : string) : number | undefined => {

        const refunds = props.payment.getRefunds().length > 0 ? props.payment.getRefunds() : props.payment.getRefundedPayments()
        for (const refund of refunds) {
            if (refund.attributes.paymentId === paymentId) {
                return refund.attributes.amount
            }
        }
        return undefined
    }

    const refunds = paymentRefundsData?.data || []
    const metadata = paymentRefundsData?.metadata
    const totalItemsCount = metadata?.totalItemsCount || 0
    updateTotalItemsCount(totalItemsCount)

    const listHeader = (): { data: (JSX.Element | string)[]; classNames: string[] } => {
        const rows = [
            <FormattedMessage
                key={"payment.payment_details_refund_listing.header_id_label"}
                id="payment.payment_details_refund_listing.header_id_label"
                defaultMessage="Payment ID"/>,
            <FormattedMessage
                key={"payment.payment_details_refund_listing.header_date_label"}
                id="payment.payment_details_refund_listing.header_date_label"
                defaultMessage="Date"/>,
            <FormattedMessage
                key={"payment.payment_details_refund_listing.header_amount_refunded_label"}
                id="payment.payment_details_refund_listing.header_amount_refunded_label"
                defaultMessage="Amount refunded"/>
        ]
        const classNames = [" ", " ", "text-right"]
        return {
            classNames: classNames,
            data: rows,
        }
    }

    const listFooter = (): { data: (JSX.Element | string)[]; classNames: string[] } => {
        let rows = [
            <strong key={"invoice.listing.headers.totals_label"}>
                <FormattedMessage
                    id="invoice.listing.headers.totals_label"
                    defaultMessage="Totals"
                />
            </strong>,
        ]

        rows = rows.concat([<></>,
            <div key={props.payment.getId()} className={"d-flex align-items-center justify-content-end"}>
                <FormattedNumber
                    style="currency"
                    currency={AppContext.getAccount().attributes.currency}
                    value={props.payment.getTotalRefundedAmount()}
                />
            </div>,
        ])
        const classNames = [" ", " ", " " ]
        return {
            classNames: classNames,
            data: rows,
        }
    }

    const getRows = () => {
        return refunds?.map(refund => {
            return mapRow(refund)
        }) || []
    }

    const mapRow = (refund : PaymentElastic): BasicRow => {
        return {
            rowId: refund.id,
            data: [
                <div key={refund.identifier}>
                    <Link to={"/payments/" + refund.id + "/edit"}>
                        {refund.identifier}
                    </Link>
                </div>,
                <div key={refund.capturedAt}>
                    <FormattedDate
                        year='numeric'
                        month='long'
                        day='numeric'
                        value={refund.capturedAt} />
                </div>,
                <div key={refund.paymentAmount}>
                    {getRefundAmountByPaymentId(refund.id)?.toString()}
                </div>,
            ],
            classNames :  [" ", " ", "text-right pr-4"]
        }
    }

    const standardList = {
        smallLine: true,
        className: "row pl-0 pr-0",
        headers: listHeader(),
        rows: getRows(),
        pageSize: paginatedSearch.pageSize,
        totalItemsCount: totalItemsCount,
        showSelectionSize: false,
        footer: listFooter(),
        footerClassName: "bg-secondary text-white"
    }
    return (
        (paymentRefundsData?.data?.length as number) > 0 &&
        <>
            <div className="my-2">
                <div className="row">
                    <div className="col-12">
                        { props.payment.getRefunds().length > 0 ?
                            <div>
                                <h3 className={"mb-0 custom-lead text-secondary"}>
                                    <FormattedMessage
                                        key={"payment.payment_details_refund_listing.title_refunds"}
                                        id="payment.payment_details_refund_listing.title_refunds"
                                        defaultMessage="Refunds"/>

                                </h3>
                            </div>

                            : <div>
                                <h3 className={"mb-0 custom-lead text-secondary"}>
                                    <FormattedMessage
                                        key={"payment.payment_details_refund_listing.title_refunded_payments"}
                                        id="payment.payment_details_refund_listing.title_refunded_payments"
                                        defaultMessage="Refunded payments"/>

                                </h3>
                            </div>
                        }


                        <ListingWrapper
                            isLoading={isLoading}
                            error={error as Error}
                            standardListProps={standardList}
                            footer= {pagination()}
                        />
                    </div>
                </div>
            </div>
        </>
    )
}

export default injectIntl(PaymentDetailsRefundListing)



// WEBPACK FOOTER //
// ./src/payment/components/PaymentDetailsRefundListing.tsx